import { useState, useEffect, useRef } from 'react';
import { usePageAccess, clearPageAccessCache } from '../../hooks/usePermissions';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { chatAPI, notificationsAPI } from '../../utils/api';
import GlobalSearch from '../GlobalSearch';
import { isAdminRole } from '../../utils/rolesConfig';
import { ArrowLeftRight, BarChart3, Bell, BookOpen, Building2, CalendarDays, CheckCircle2, CheckSquare, ChevronDown, ExternalLink, FolderKanban, GanttChartSquare, GraduationCap, Kanban, Layers, LayoutDashboard, LayoutGrid, LifeBuoy, ListChecks, LogOut, Menu, MessageSquare, Moon, Settings, Shield, Sun, User, Users, Wrench, X, Zap } from 'lucide-react';
import { useUnsavedGuard } from '../UnsavedChanges';

// ── User avatar ───────────────────────────────────────────────────────────
export const UserAvatar = ({ user, size = 'sm' }) => {
  if (!user) return null;
  const s = size === 'sm'  ? 'w-7 h-7 text-xs'
           : size === 'md' ? 'w-9 h-9 text-sm'
           :                 'w-10 h-10 text-sm';
  return (
    <div className={`${s} rounded-lg flex items-center justify-center font-bold text-white flex-shrink-0`}
      style={{ backgroundColor: user.avatar_color || '#2E539F' }}>
      {user.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
    </div>
  );
};

// ── Role badge colours ────────────────────────────────────────────────────
const ROLE_BADGE = {
  Admin:      { bg:'#EEF2FA', text:'#2E539F' },
  Manager:    { bg:'#E0F2FE', text:'#0369a1' },
  Specialist: { bg:'#D1FAE5', text:'#065f46' },
};

// ── Navigation items ──────────────────────────────────────────────────────
// ALL_NAV — no hardcoded roles. Visibility controlled by Page Access (role_page_access table).
// Always-visible pages (Dashboard, Chat, etc.) show for all users.
// Other pages show based on the user's permission level in the DB.
const ALL_NAV = [
  { to:'/',                   icon:LayoutDashboard,  label:'Dashboard',     end:true },
  { to:'/clients',            icon:Building2,        label:'Clients'                },
  { to:'/projects',           icon:FolderKanban,     label:'Projects'               },
  { to:'/tasks',              icon:CheckSquare,      label:'Tasks'                  },
  { to:'/activities',         icon:Layers,           label:'Activities'             },
  { to:'/dev-hub',            icon:Wrench,           label:'Dev Hub'                },
  { to:'/training',           icon:GraduationCap,    label:'Training'               },
  { to:'/knowledge',          icon:BookOpen,         label:'Knowledge Base'         },
  { to:'/workplan',           icon:CalendarDays,     label:'Workplan'               },
  { to:'/todo',               icon:ListChecks,       label:'To-Do'                  },
  { to:'/kanban',             icon:Kanban,           label:'Kanban'                 },
  { to:'/platforms',          icon:LayoutGrid,       label:'Platforms'              },
  { to:'/system-support',    label:'System Support',  icon:LifeBuoy },
  { to:'/milestones',         icon:CheckCircle2,     label:'Milestones'             },
  { to:'/risks',              icon:Shield,           label:'Risk Register'          },
  { to:'/milestone-timeline', icon:GanttChartSquare, label:'Timeline'               },
  { to:'/chat',               icon:MessageSquare,    label:'Chat'                   },
  { to:'/reports',            icon:BarChart3,        label:'Reports'                },
  { to:'/users',              icon:Users,            label:'Users'                  },
];

// ── Notifications bell dropdown ───────────────────────────────────────────
function NotificationsBell() {
  const navigate  = useNavigate();
  const ref       = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const r = await notificationsAPI.getAll({ limit: 10 });
      setNotifications(r.data?.rows || []);
    } catch(e) { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const unread = notifications.filter(n => !n.is_read).length;

  const handleClick = async (n) => {
    if (!n.is_read) { try { await notificationsAPI.markRead(n.id); load(); } catch(e) {} }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-xl transition-colors hover:bg-white/10 flex items-center"
        style={{ color: 'rgba(255,255,255,0.6)' }}
        title="Notifications">
        <Bell size={18}/>
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold leading-none px-1">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-2xl shadow-2xl z-[200] overflow-hidden"
          style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
          <div className="flex items-center justify-between px-4 py-3 border-b"
            style={{ borderColor:'var(--border)' }}>
            <p className="font-semibold text-sm" style={{ color:'var(--text)' }}>Notifications</p>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={async()=>{await notificationsAPI.markAllRead();load();}}
                  className="text-xs text-brand-500 hover:underline">Mark all read</button>
              )}
              <button onClick={() => setOpen(false)} style={{ color:'var(--text-secondary)' }}>
                <X size={14}/>
              </button>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="text-sm text-center py-8" style={{ color:'var(--text-secondary)' }}>
                No notifications yet
              </p>
            ) : notifications.map(n => (
              <div key={n.id} onClick={() => handleClick(n)}
                className={`px-4 py-3 border-b cursor-pointer transition-colors hover:bg-[var(--surface-alt)] ${!n.is_read ? 'border-l-2 border-l-brand-500' : ''}`}
                style={{ borderColor:'var(--border)' }}>
                <p className="text-sm font-medium" style={{ color:'var(--text)' }}>{n.title}</p>
                {n.body && <p className="text-xs mt-0.5 truncate" style={{ color:'var(--text-secondary)' }}>{n.body}</p>}
                <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
                  {new Date(n.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
          <Link to="/notifications" onClick={() => setOpen(false)}
            className="block text-center text-xs py-2.5 border-t text-brand-500 hover:underline"
            style={{ borderColor:'var(--border)' }}>
            View all notifications →
          </Link>
        </div>
      )}
    </div>
  );
}

// ── User dropdown ──────────────────────────────────────────────────────────
function UserDropdown({ user, theme, toggleTheme, onLogout }) {
  const navigate = useNavigate();
  const ref      = useRef(null);
  const [open, setOpen] = useState(false);
  const rb = ROLE_BADGE[user?.role] || { bg:'#F1F5F9', text:'#64748b' };

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2.5 px-2 py-1.5 rounded-xl transition-colors hover:bg-white/10">
        <UserAvatar user={user} size="sm" />
        <div className="text-left hidden md:block">
          <p className="text-sm font-semibold leading-none text-white">
            {user?.name}
          </p>
          <span className="text-xs font-medium px-1.5 py-0.5 rounded mt-0.5 inline-block"
            style={{ background: rb.bg, color: rb.text }}>
            {user?.role}
          </span>
        </div>
        <ChevronDown size={14} className={`transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}
          style={{ color:'rgba(255,255,255,0.5)' }} />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 rounded-2xl shadow-2xl z-[200] overflow-hidden"
          style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>

          {/* User identity block */}
          <div className="px-4 py-4 border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
            <div className="flex items-center gap-3">
              <UserAvatar user={user} size="md" />
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate" style={{ color:'var(--text)' }}>{user?.name}</p>
                <p className="text-xs truncate" style={{ color:'var(--text-secondary)' }}>{user?.email}</p>
                <span className="text-xs font-medium px-1.5 py-0.5 rounded mt-1 inline-block"
                  style={{ background: rb.bg, color: rb.text }}>
                  {user?.role}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="py-1">
            {/* Profile */}
            <button onClick={() => go('/profile')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors hover:bg-[var(--surface-alt)]"
              style={{ color:'var(--text)' }}>
              <User size={15} style={{ color:'var(--text-secondary)' }} />
              My Profile
            </button>

            {/* Delegation */}
            <button onClick={() => go('/delegation')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors hover:bg-[var(--surface-alt)]"
              style={{ color:'var(--text)' }}>
              <ArrowLeftRight size={15} style={{ color:'var(--text-secondary)' }} />
              <div className="flex-1 text-left">
                <p className="text-sm">Delegation</p>
                <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Delegate tasks while away</p>
              </div>
            </button>

            {/* Theme toggle */}
            <button onClick={() => { toggleTheme(); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors hover:bg-[var(--surface-alt)]"
              style={{ color:'var(--text)' }}>
              {theme === 'dark'
                ? <Sun size={15} style={{ color:'var(--text-secondary)' }} />
                : <Moon size={15} style={{ color:'var(--text-secondary)' }} />}
              {theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            </button>

            {/* Admin panel — Admin and Administrator */}
            {isAdminRole(user?.role) && (
              <Link to="/admin" onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-[var(--surface-alt)]"
                style={{ color:'var(--text)' }}>
                <Settings size={15} style={{ color:'var(--text-secondary)' }} />
                Admin Panel
                <ExternalLink size={11} className="ml-auto opacity-40" />
              </Link>
            )}

            <div className="border-t my-1" style={{ borderColor:'var(--border)' }} />

            {/* Logout */}
            <button onClick={() => { setOpen(false); clearPageAccessCache(); onLogout(); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500">
              <LogOut size={15} />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Top Header ─────────────────────────────────────────────────────────────
function TopHeader({ onMenuClick, user, theme, toggleTheme, onLogout, chatUnread }) {
  return (
    <header className="h-14 flex items-center justify-between px-4 lg:px-6 border-b flex-shrink-0 z-[100]"
      style={{ background:'var(--sidebar-bg)', borderColor:'rgba(255,255,255,0.08)', boxShadow:'0 1px 4px rgba(0,0,0,.18)' }}>

      {/* Left — brand + mobile hamburger */}
      <div className="flex items-center gap-3">
        {/* Mobile hamburger */}
        <button onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors text-white/70 hover:text-white">
          <Menu size={20} />
        </button>

        {/* Brand */}
        <Link to="/" className="flex items-center gap-2.5 select-none group">
          {/* Q-Lana TMS Logo */}
          <div className="w-9 h-9 flex-shrink-0">
            <svg width="36" height="36" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Q-Lana TMS">
              <rect width="40" height="40" rx="9" fill="#1A2F5E"/>
              <line x1="9" y1="13" x2="27" y2="13" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              <line x1="9" y1="20" x2="27" y2="20" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              <line x1="9" y1="27" x2="20" y2="27" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              <polyline points="21,24 25,28 33,18" stroke="#5CC3BC" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          </div>
          <div className="hidden sm:block">
            <p className="font-bold text-sm leading-none text-white">
              Task Management System
            </p>
            <p className="text-xs leading-none mt-0.5 text-white/40">
              by Q-Lana
            </p>
          </div>
        </Link>
      </div>

      {/* Center — Global Search */}
      <div className="hidden md:flex flex-1 justify-center px-4 max-w-md mx-auto">
        <GlobalSearch />
      </div>

      {/* Right — actions */}
      <div className="flex items-center gap-1">
        {/* Chat unread badge shortcut */}
        {chatUnread > 0 && (
          <Link to="/chat"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium transition-colors hover:bg-white/10"
            style={{ color:'rgba(255,255,255,0.6)' }}
            title="Unread chat messages">
            <MessageSquare size={15} />
            <span className="bg-brand-500 text-white rounded-full px-1.5 py-0.5 text-xs font-bold leading-none">
              {chatUnread > 99 ? '99+' : chatUnread}
            </span>
          </Link>
        )}

        {/* Notification bell */}
        <NotificationsBell />

        {/* Divider */}
        <div className="w-px h-6 mx-1" style={{ background:'rgba(255,255,255,0.12)' }} />

        {/* User dropdown */}
        <UserDropdown
          user={user}
          theme={theme}
          toggleTheme={toggleTheme}
          onLogout={onLogout}
        />
      </div>
    </header>
  );
}

// ── Sidebar (navigation only) ──────────────────────────────────────────────
function Sidebar({ user, chatUnread, onNavClick, allowedPaths }) {
  const navigate = useNavigate();
  const { confirmLeave } = useUnsavedGuard();

  /**
   * In-app navigation starts here, so this is where unsaved edits are caught.
   * `useBlocker` would be the right tool but needs a data router, and this app
   * uses <BrowserRouter><Routes>.
   *
   * Modified clicks (new tab, new window) are left alone — they do not leave
   * the current page, so there is nothing to lose.
   */
  const guardedClick = (to) => async (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    if (await confirmLeave()) { onNavClick?.(); navigate(to); }
  };

  // Show nav items where the user has page access, or always-visible paths
  // While permissions are loading (allowedPaths=null), show nothing to avoid flash
  const visibleNav = ALL_NAV.filter(i => allowedPaths?.has(i.to));
  return (
    <div className="flex flex-col h-full" style={{ background:'var(--sidebar-bg)' }}>
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto pt-4">
{visibleNav.map(({ to, icon:Icon, label, end }) => (
          <NavLink key={to} to={to} end={end} onClick={guardedClick(to)}
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <Icon size={16} className="flex-shrink-0" />
            <span className="flex-1">{label}</span>
            {to === '/chat' && chatUnread > 0 && (
              <span className="ml-auto text-xs bg-brand-500 text-white rounded-full px-1.5 py-0.5 leading-none font-bold min-w-[18px] text-center">
                {chatUnread > 99 ? '99+' : chatUnread}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <p className="text-center text-xs text-white/25">&copy; Q-Lana Inc.</p>
      </div>
    </div>
  );
}

// ── Root layout ────────────────────────────────────────────────────────────
export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const pollRef = useRef(null);

  // Dynamic sidebar — reads which pages this user's role can see
  const { allowedPaths, loading: permLoading } = usePageAccess(user?.id);

  useEffect(() => {
    const poll = async () => {
      try {
        const r = await chatAPI.getGroups();
        const total = (r.data || []).reduce((s, g) => s + (parseInt(g.unread_count) || 0), 0);
        setChatUnread(total);
      } catch(e) { /* ignore */ }
    };
    poll();
    pollRef.current = setInterval(poll, 15000);
    return () => clearInterval(pollRef.current);
  }, []);

  const handleLogout = () => { clearPageAccessCache(); logout(); navigate('/login'); };

  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ background:'var(--bg)' }}>

      {/* ── Top header: full width, always visible ── */}
      <TopHeader
        onMenuClick={() => setMobileOpen(true)}
        user={user}
        theme={theme}
        toggleTheme={toggleTheme}
        onLogout={handleLogout}
        chatUnread={chatUnread}
      />

      {/* ── Below header: sidebar + main content ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-56 flex-shrink-0 flex-col border-r"
          style={{ borderColor:'var(--border)', background:'var(--sidebar-bg)' }}>
          <Sidebar user={user} chatUnread={chatUnread} onNavClick={() => {}} allowedPaths={allowedPaths} />
        </aside>

        {/* Mobile sidebar overlay */}
        {mobileOpen && (
          <div className="fixed inset-0 z-[150] lg:hidden">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
            <aside className="absolute left-0 top-0 h-full w-60"
              style={{ background:'var(--sidebar-bg)' }}>
              {/* Mobile sidebar header */}
              <div className="h-14 flex items-center justify-between px-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-brand-500 rounded flex items-center justify-center">
                    <Zap size={12} className="text-white" />
                  </div>
                  <span className="text-white font-bold text-sm">TMS</span>
                </div>
                <button onClick={() => setMobileOpen(false)}
                  className="p-1.5 rounded text-white/40 hover:text-white transition-colors">
                  <X size={16} />
                </button>
              </div>
              <Sidebar user={user} chatUnread={chatUnread} onNavClick={() => setMobileOpen(false)} allowedPaths={allowedPaths} />
            </aside>
          </div>
        )}

        {/* Main content area */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-6 max-w-screen-2xl mx-auto animate-fade-in">
            <Outlet />
          </div>
        </main>

      </div>
    </div>
  );
}
